import React, { useEffect, useState } from 'react';

import {Search, Info, ChevronDown} from "lucide-react";
import {TooltipProvider, Tooltip, TooltipTrigger, TooltipContent} from "../../ui/Tooltip/Tooltip.tsx";
import {Input} from "../../ui/General/LucideInput/LucideInput.tsx";
import { SelectOption } from '../../ui/SelectFiltered/types/SelectOption.interface.ts';
import {Button} from "../../ui/General/LucideButton/LucideButton.tsx";
import { cn } from '../../utils/cn.ts';
import styles from './SearchCarPartsWidget.module.css';
import { API_URL } from '../../config.tsx';

interface Props {
    brand: string,
    modelSlug: string,
    bodySlug: string,
    categoryName: string,
    init: boolean,
    setInit: (init: boolean) => void,
    brandCode: string,
    setBrandCode: (brandCode: string) => void,
    setSsd: (ssd: string) => void,
    vinOrCarNumber: string,
    setVinOrCarNumber: (vinOrCarNumber: string) => void,
    searchQuery: string,
    setSearchQuery: (searchQuery: string) => void,
    step: number,
    setStep: (step: number) => void,
    stepValues: {[key in number]: string},
    setStepValues: (stepValues: {[key in number]: string}) => void,
    catalogs: {[key in number]: {name: string, options: SelectOption[]}},
    setCatalogs: (catalogs: {[key in number]: {name: string, options: SelectOption[]}}) => void,
    search: () => void
}

const BrandsField = ({
  brandCode,
  catalogs,
  getCatalog,
  init
}: {
  brandCode: string,
  catalogs: {[key in number]: {name: string, options: SelectOption[]}},
  getCatalog: (catalogs: {
    [key in number]: {name: string, options: SelectOption[]}},
    brandCode: string,
    ssd: string,
    step: number) => void
  init: boolean
  }) => {
    const [brand, changeBrand] = useState('');
    const [brands, setBrands] = useState([]);

    const getBrands = (brandCode?: string) => {
        fetch(`${API_URL}/car_brands`, {
            method: 'GET',
            credentials: 'include',
        })
            .then(response => response.json())
            .then(brands => {
                setBrands(brands)
                if(brandCode) {
                    brands.map((brand: {id: string, value: string}) => {
                        if (brand.value.toLowerCase() === brandCode.toLowerCase()) {
                            changeBrand(brand.value);
                            getCatalog(catalogs, brand.id, '', 1);
                        }
                    })
                }
            })
    }

    const onChangeBrand = (brandName: React.SetStateAction<string>) => {
        brands.map((brand: {id: string, value: string}) => {
            if (brand.value == brandName) {
                changeBrand(brand.value);
                getCatalog(catalogs, brand.id, '', 1);
            }
        })
    }

    useEffect(() => {
      if(init) getBrands(brandCode);
    }, [init]);

    return (
      <div className={styles.selectWrapper}>
      <select
      className={cn("w-full px-3 py-2 border border-gray-300 rounded-md appearance-none bg-white text-sm", styles.select)}
      defaultValue={brand}
      value={brand}
      onChange={(e) => onChangeBrand(e.target.value)}
      >
        <option value="" disabled hidden>Марка</option>
        {brands.map((brand: SelectOption) => (
          <option key={brand.id} value={brand.value}>{brand.value}</option>
        ))}
      </select>
      <ChevronDown className="absolute right-2 top-2 w-4 h-4 text-gray-500" />
    </div>
    )
}

export const SearchCarPartsWidget: React.FC<Props & React.HTMLAttributes<HTMLDivElement>> = ({
  brand,
  modelSlug,
  bodySlug,
  categoryName,
  init,
  setInit,
  brandCode,
  setBrandCode,
  setSsd,
  vinOrCarNumber,
  setVinOrCarNumber,
  searchQuery,
  setSearchQuery,
  step,
  setStep,
  stepValues,
  setStepValues,
  catalogs,
  setCatalogs,
  search
}) => {


    const getCatalog = (catalogs: {[key in number]: {name: string, options: SelectOption[]}}, brandCode: string, ssd: string, step: number) => {
      if(!brandCode) return;
      setBrandCode(brandCode);

      fetch(`${API_URL}/car_params?brandCode=${brandCode}&ssd=${ssd}`, {
          method: 'GET',
          credentials: 'include',
      })
          .then(response => response.json())
          .then(catalog => {
              if(Object.keys(catalog).length === 0) return;
              let newCatalogs = [];
              let models: SelectOption[] = [];
              let bodies: SelectOption[] = [];
              Object.keys(catalogs).map(key => {
                  const stepKey = Number(key);
                  if (stepKey < step) newCatalogs[stepKey] = catalogs[stepKey];
              })
              if(step === 1) {
                  catalog.options.map((option: SelectOption) => {
                      models.push(option);
                  })
              }
              if(step === 2) {
                  catalog.options.map((option: SelectOption) => {
                      bodies.push(option);
                  })
              }
              newCatalogs[step] = catalog;
              setCatalogs(newCatalogs);
              if (catalog.options && catalog.options.length) setStep(step);
              if(modelSlug && step === 1) {
                  models.map(model => {
                      if(
                          model.value.toLowerCase() === modelSlug.toLowerCase()
                          || model.value.toLowerCase() === modelSlug.replace(' ', '').toLowerCase()
                          || model.value.toLowerCase() === modelSlug.replace('_', '').toLowerCase()
                          || model.value.toLowerCase() === modelSlug.replace('_', ' ').toLowerCase()
                          || model.value.toLowerCase() === modelSlug.replace(' ', '_').toLowerCase()
                      ) {
                          onChangeValue(newCatalogs,model.value, step + 1);
                          setStepValues({...stepValues, [step]: model.value});
                          return
                      }
                  })
              }
              if(bodySlug && step === 2) {
                  bodies.map(body => {
                      if(
                          body.value.toLowerCase() === bodySlug.toLowerCase()
                          || body.value.toLowerCase() === bodySlug.replace(' ', '').toLowerCase()
                          || body.value.toLowerCase() === bodySlug.replace('_', '').toLowerCase()
                          || body.value.toLowerCase() === bodySlug.replace('_', ' ').toLowerCase()
                          || body.value.toLowerCase() === bodySlug.replace(' ', '_').toLowerCase()
                      ) {
                          onChangeValue(newCatalogs, body.value, step + 1);
                          setStepValues({...stepValues, [step]: body.value});
                          return;
                      }
                  })
              }
          }).then(() => setInit(false))
  }

  const onChangeValue = (
    catalogs: {[key in number]: {name: string, options: SelectOption[]}},
    ssdValue: string,
    step: number
  ) => {
      setVinOrCarNumber('');
      getCatalog(catalogs, brandCode, ssdValue, step);
      setSsd(ssdValue);
  }

    return (
            <div className={styles.searchSection}>
              <div className={styles.sectionTitle}>
                <div className="flex items-center">
                  <div className={styles.searchIcon}>
                    <Search className="w-4 h-4 text-white" />
                  </div>
                  <h1 className={styles.title}>
                    <span className={styles.titleBrand}>{brand.toLocaleUpperCase()}</span>
                    <span className={styles.titleSeparator}>|</span>
                    {categoryName} для {brand.toLocaleUpperCase()} {modelSlug.toLocaleUpperCase()} {bodySlug.toLocaleUpperCase()}
                  </h1>
                </div>
              </div>

              <div className={styles.searchLayout}>
                <div className="flex-1">
                  <label className={styles.selectionLabel}>Подбор по модели</label>
                  <div className={styles.selectionGrid}>
                     <BrandsField brandCode={brand} catalogs={catalogs} getCatalog={getCatalog} init={init} />
                     {
                          Object.keys(catalogs).map(key => {
                              const stepKey = Number(key);
                              if(stepKey === 0 || stepKey > 2) return null;
                              return (
                                <div className={styles.selectWrapper}>
                                  <select
                                    className={cn("w-full px-3 py-2 border border-gray-300 rounded-md appearance-none bg-white text-sm", styles.select)}
                                    value={stepValues[stepKey] ? stepValues[stepKey] : ''}
                                    onChange={(e) => {
                                      setStepValues({...stepValues, [stepKey]: e.target.value});
                                      onChangeValue(catalogs, e.target.value, stepKey + 1);
                                    }}
                                  >
                                    <option value="">{catalogs[stepKey].name}</option>
                                    {catalogs[stepKey].name}
                                    {catalogs[stepKey].options.map((option: SelectOption) => (
                                      <option key={option.id} value={option.id}>{option.value}</option>
                                    ))}
                                  </select>
                                  <ChevronDown className="absolute right-2 top-2 w-4 h-4 text-gray-500" />
                                </div>
                          )
                          })
                      }
                    </div>
                   <div className={`overflow-hidden transition-all ease-out duration-300 ${step > 2 ? 'max-h-40 opacity-100 mt-3' : 'max-h-0 opacity-0 mt-0'}`}>
                    <div className={styles.selectionGrid}>
                     {
                          Object.keys(catalogs).map(key => {
                              const stepKey = Number(key);
                              if(stepKey < 3 || stepKey > 5) return null;
                              return (
                                <div className={styles.selectWrapper}>
                                  <select
                                    className={cn("w-full px-3 py-2 border border-gray-300 rounded-md appearance-none bg-white text-sm", styles.select)}
                                    value={stepValues[stepKey] ? stepValues[stepKey] : ''}
                                    onChange={(e) => {
                                      setStepValues({...stepValues, [stepKey]: e.target.value});
                                      onChangeValue(catalogs, e.target.value, stepKey + 1);
                                    }}
                                  >
                                    <option value="">{catalogs[stepKey].name}</option>
                                    {catalogs[stepKey].name}
                                    {catalogs[stepKey].options.map((option: SelectOption) => (
                                      <option key={option.id} value={option.id}>{option.value}</option>
                                    ))}
                                  </select>
                                  <ChevronDown className="absolute right-2 top-2 w-4 h-4 text-gray-500" />
                                </div>
                          )
                          })
                      }
                      {step === 3 ? <><div className={styles.selectWrapper} /><div className={styles.selectWrapper} /></> : null}
                      {step === 4 ? <div className={styles.selectWrapper} /> : null}
                     </div>
                   </div>
                   <div className={`overflow-hidden transition-all ease-out duration-300 ${step > 5 ? 'max-h-40 opacity-100 mt-3' : 'max-h-0 opacity-0 mt-0'}`}>
                    <div className={styles.selectionGrid}>
                     {
                          Object.keys(catalogs).map(key => {
                              const stepKey = Number(key);
                              if(stepKey < 6 || stepKey > 8) return null;
                              return (
                                <div className={styles.selectWrapper}>
                                  <select
                                    className={cn("w-full px-3 py-2 border border-gray-300 rounded-md appearance-none bg-white text-sm", styles.select)}
                                    value={stepValues[stepKey] ? stepValues[stepKey] : ''}
                                    onChange={(e) => {
                                      setStepValues({...stepValues, [stepKey]: e.target.value});
                                      onChangeValue(catalogs, e.target.value, stepKey + 1);
                                    }}
                                  >
                                    <option value="">{catalogs[stepKey].name}</option>
                                    {catalogs[stepKey].name}
                                    {catalogs[stepKey].options.map((option: SelectOption) => (
                                      <option key={option.id} value={option.id}>{option.value}</option>
                                    ))}
                                  </select>
                                  <ChevronDown className="absolute right-2 top-2 w-4 h-4 text-gray-500" />
                                </div>
                          )
                          })
                      }
                      {step === 6 ? <><div className={styles.selectWrapper} /><div className={styles.selectWrapper} /></> : null}
                      {step === 7 ? <div className={styles.selectWrapper} /> : null}
                     </div>
                   </div>
                </div>

                {/* 'or' separator */}
                <div className="flex items-center self-center" style={{paddingTop: 28,}}>
                    <span className="text-gray-600 font-semibold">или</span>
                </div>

                {/* Select Vehicle by VIN */}
                <div className="flex-1">
                   <label className={styles.selectionLabel}>
                    <span className="mr-2">Подбор по VIN или гос. номеру </span>
                    <TooltipProvider>
                      <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-gray-500 cursor-help" style={{display: 'inline'}} />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Идентификационный номер транспортного средства, например JTJHK31U802038156, или гос. номер, например А125АА 125</p>
                      </TooltipContent>
                    </Tooltip>
                    </TooltipProvider>
                  </label>
                   <div className={styles.selectionGrid}>
                      <div className={styles.selectWrapper}>
                        <Input
                          placeholder="Введите VIN или гос. номер..."
                          className="w-full text-sm py-1 px-3 h-10"
                          style={{paddingTop: 0, paddingBottom: 0, paddingRight: 0}}
                          value={vinOrCarNumber}
                          onChange={(e) => setVinOrCarNumber(e.target.value)}
                          onKeyDown={(e) => {
                            if(e.key === 'Enter') {
                              search();
                            }
                          }}
                        />
                      </div>
                   </div>
                </div>
              </div>
               <div className={styles.vinExplanation}>
                 Для максимально точного подбора выберите автомобиль по VIN (Идентификационный номер транспортного средства).
               </div>

              <div className={styles.searchBar}>
                <div className="flex-1">
                  <label className={styles.selectionLabel}>
                    Поиск по названию, артикулу или OEM номеру
                  </label>
                  <div className={styles.selectWrapper}>
                    <Input
                      placeholder="Введите название, артикул или OEM номер..."
                      className={cn("text-lg py-1 h-10 mr-2", styles.searchInput)}
                      style={{paddingTop: 0, paddingBottom: 0, paddingRight: 0}}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => {
                        if(e.key === 'Enter') {
                            search();
                        }
                      }}
                    />
                  </div>
                </div>
                <Button
                  className={cn("bg-[#ea8d00] hover:bg-[#d47b00] text-white py-2 px-4", styles.searchButton)}
                  onClick={search}
                  id='search-product-btn'
                  size="default"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Найти
                </Button>
              </div>
            </div>
    );
};
